import { expandScope } from './airport-reference';

// `airport-reference:expandScopes@v1` (users-roles-permissions R1, brief §4.18).
//
// A user's scope is a LIST of entries; their effective airports are the union of
// `expandScope` over the entries ∩ the tenant's operating set. Fail closed: anything that
// is not a well-formed entry contributes nothing, and only an explicit ALL entry is ever
// unrestricted.

/**
 * Structural twin of `@nexa/auth`'s `ScopeEntry` — redeclared rather than imported so
 * this package keeps no dependency on `@nexa/auth` (same rule as its `Scope`). Must stay
 * in lockstep with it: REGION|COUNTRY|AIRPORT carry a `code`, ALL carries none.
 */
export type ScopeEntryLike =
  | { kind: 'REGION' | 'COUNTRY' | 'AIRPORT'; code: string }
  | { kind: 'ALL' };

const CODED_KINDS: ReadonlySet<string> = new Set(['REGION', 'COUNTRY', 'AIRPORT']);

/**
 * Expand a scope list to the IATA codes it covers.
 *
 * - Union of {@link expandScope} over every entry, de-duplicated and sorted.
 * - `operating` given → the union is intersected with it (the tenant's operating set).
 * - Any `{ kind: 'ALL' }` entry → `'*'` when `operating` is undefined ("everything the
 *   tenant operates", resolved by the caller), else a normalized copy of `operating`.
 *
 * Fail closed: a non-array `entries` → `[]`; a malformed entry (not an object, unknown
 * kind, missing / empty / non-string code) or an unknown code contributes nothing. `'*'`
 * is returned for an explicit ALL entry and nothing else. Pass the output of
 * `normalizeScopeEntries` (@nexa/auth), which additionally rejects a partially-invalid
 * list as a whole. `operating` other than `undefined` or an array is treated as empty.
 */
export function expandScopes(entries: unknown, operating?: readonly string[]): string[] | '*' {
  if (!Array.isArray(entries)) return [];

  let all = false;
  const union = new Set<string>();
  for (const entry of entries as readonly unknown[]) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const { kind, code } = entry as { kind?: unknown; code?: unknown };
    if (kind === 'ALL') {
      all = true;
      continue;
    }
    if (typeof kind !== 'string' || !CODED_KINDS.has(kind)) continue;
    if (typeof code !== 'string' || code.trim().length === 0) continue;
    for (const iata of expandScope({ kind: kind as 'REGION' | 'COUNTRY' | 'AIRPORT', code })) {
      union.add(iata);
    }
  }

  if (operating === undefined) {
    return all ? '*' : [...union].sort();
  }

  const operatingSet = new Set(normalizeCodes(operating));
  if (all) return [...operatingSet].sort();
  return [...union].filter((iata) => operatingSet.has(iata)).sort();
}

function normalizeCodes(codes: unknown): string[] {
  if (!Array.isArray(codes)) return [];
  return (codes as readonly unknown[])
    .filter((c): c is string => typeof c === 'string')
    .map((c) => c.trim().toUpperCase())
    .filter((c) => c.length > 0);
}
